import type { FiscalCalendar } from "@/features/finance/model/finance-close-trust-types"
import { PeriodPreset } from "@/shared/lib/dates/period-range"

/** Date-only (`YYYY-MM-DD`, branch timezone) reporting range produced by the finance period picker. */
export type FinancePeriodRange = { from?: string; to?: string }

/** All fiscal periods across every fiscal year, sorted ascending by start date. */
export function flattenFiscalPeriods(calendar: FiscalCalendar | undefined) {
  return (calendar?.years ?? [])
    .flatMap((year) => year.periods)
    .sort((left, right) => left.startDate.localeCompare(right.startDate))
}

/**
 * Resolve the current / previous fiscal period to date-only bounds, bracketing the workspace "today"
 * (a `YYYY-MM-DD` string). Returns null when there is no fiscal calendar or today falls outside every period.
 * String comparison is exact for `YYYY-MM-DD` and sidesteps timezone drift.
 */
export function resolveFiscalPeriodRange(
  preset: PeriodPreset.CurrentFiscalPeriod | PeriodPreset.PreviousFiscalPeriod,
  todayIso: string,
  calendar: FiscalCalendar | undefined,
): FinancePeriodRange | null {
  const periods = flattenFiscalPeriods(calendar)
  if (periods.length === 0) return null

  const currentIndex = periods.findIndex((period) => period.startDate <= todayIso && todayIso <= period.endDate)
  if (currentIndex === -1) return null

  const target = preset === PeriodPreset.CurrentFiscalPeriod ? periods[currentIndex] : periods[currentIndex - 1]
  if (!target) return null

  return { from: target.startDate, to: target.endDate }
}
